import { useCallback, useEffect, useMemo, useState } from "react";
import { Gift, Trophy, Settings as SettingsIcon, AlertTriangle, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const GoldButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);
const GhostButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-xs uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);

export default function AdminReferrals() {
    const [tab, setTab] = useState("config");
    const [config, setConfig] = useState(null);
    const [savingCfg, setSavingCfg] = useState(false);

    // Lists
    const [allRows, setAllRows] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    // Filters
    const [filterStatus, setFilterStatus] = useState("all");

    const loadAll = useCallback(async () => {
        setRefreshing(true);
        try {
            // Borrow the firm-side /me endpoint for cfg snapshot
            const [meR, allR, lbR] = await Promise.all([
                api.get("/referrals/me"),
                api.get("/referrals/admin/all"),
                api.get("/referrals/admin/leaderboard"),
            ]);
            setConfig(meR.data.config);
            setAllRows(allR.data || []);
            setLeaderboard(lbR.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRefreshing(false); }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const saveConfig = async () => {
        setSavingCfg(true);
        try {
            const payload = {
                referral_pct: parseFloat(config.pct),
                referral_tier_bonus_pct: parseFloat(config.tier_bonus),
                referral_tier_threshold: parseInt(config.tier_threshold, 10),
                referral_signup_bonus_enabled: !!config.signup_bonus_enabled,
                referral_signup_bonus_inr: parseFloat(config.signup_bonus),
                referral_max_cycles: parseInt(config.max_cycles, 10),
                referral_cooling_days: parseInt(config.cooling_days, 10),
            };
            const { data } = await api.patch("/referrals/admin/config", payload);
            setConfig(data.config);
            toast.success("Referral config saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingCfg(false); }
    };

    const clearReview = async (id) => {
        if (!window.confirm("Mark this suspicious referral as legitimate? Held commission rows will move to pending.")) return;
        try {
            const { data } = await api.post(`/referrals/admin/clear-review/${id}`);
            toast.success(`Moved ₹${data.moved_to_pending} to pending`);
            loadAll();
        } catch (e) { toast.error(apiError(e)); }
    };

    const filteredRows = useMemo(() => {
        if (filterStatus === "all") return allRows;
        if (filterStatus === "suspicious") return allRows.filter(r => r.suspicious);
        if (filterStatus === "converted") return allRows.filter(r => r.converted);
        if (filterStatus === "signed_up") return allRows.filter(r => !r.converted && !r.suspicious);
        if (filterStatus === "expired") return allRows.filter(r => r.status === "expired");
        return allRows;
    }, [allRows, filterStatus]);

    const stats = useMemo(() => ({
        total: allRows.length,
        converted: allRows.filter(r => r.converted).length,
        suspicious: allRows.filter(r => r.suspicious).length,
        expired: allRows.filter(r => r.status === "expired").length,
    }), [allRows]);

    const refCtrl = useTableControls(filteredRows, {
        accessors: { flag: (r) => r.suspicious ? "review" : "" },
    });
    const lbCtrl = useTableControls(leaderboard);

    return (
        <div className="space-y-6" data-testid="admin-referrals-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Platform</div>
                    <h1 className="display-display mt-1">Referral System</h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        Configure commission rates, sweep suspicious activity, and audit every referral signup across all tenants. All changes apply platform-wide and take effect on the next subscription invoice.
                    </p>
                </div>
                <GhostButton onClick={loadAll} disabled={refreshing} data-testid="admin-refresh-btn">
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                    Refresh
                </GhostButton>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-4 gap-4">
                <Stat icon={Gift} label="Total signups" value={stats.total} testId="admin-stat-total"/>
                <Stat icon={CheckCircle2} label="Converted" value={stats.converted} testId="admin-stat-converted"
                      tone="emerald"/>
                <Stat icon={AlertTriangle} label="Suspicious" value={stats.suspicious} testId="admin-stat-suspicious"
                      tone={stats.suspicious > 0 ? "amber" : "neutral"}/>
                <Stat icon={ShieldCheck} label="Expired" value={stats.expired} testId="admin-stat-expired"/>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border overflow-x-auto scrollbar-hide">
                {[
                    ["config", "Configuration", SettingsIcon],
                    ["referrals", "All referrals", Gift],
                    ["leaderboard", "Leaderboard", Trophy],
                ].map(([k, label, Icon]) => (
                    <button key={k}
                        onClick={() => setTab(k)}
                        data-testid={`admin-referrals-tab-${k}`}
                        className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] -mb-px border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                ))}
            </div>

            {/* CONFIG TAB */}
            {tab === "config" && config && (
                <div className="bg-card border border-border p-6 space-y-6 max-w-3xl" data-testid="admin-config-card">
                    <div>
                        <div className="overline">Commission engine</div>
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                            Note: Law OS does not offer monthly subscriptions — commission accrues per billing <em>cycle</em>{" "}
                            (quarterly, half-yearly, annual, bi-annual or tri-annual depending on the firm's plan).
                            "Max cycles" is the number of paid invoices each referral earns commission on.
                        </p>
                    </div>

                    {/* Signup bonus toggle */}
                    <div className="border border-border p-4 space-y-3" data-testid="admin-signup-bonus-card">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm">Signup bonus</div>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    One-time bonus credited to the referrer when their referee pays their first invoice.
                                </p>
                            </div>
                            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={!!config.signup_bonus_enabled}
                                    onChange={e => setConfig({ ...config, signup_bonus_enabled: e.target.checked })}
                                    className="w-4 h-4 accent-primary"
                                    data-testid="admin-signup-bonus-toggle"
                                />
                                <span>{config.signup_bonus_enabled ? "Enabled" : "Disabled"}</span>
                            </label>
                        </div>
                        <FieldNum label="Bonus amount (INR)" value={config.signup_bonus}
                            onChange={(v) => setConfig({ ...config, signup_bonus: v })}
                            testId="admin-signup-bonus-amount"
                            disabled={!config.signup_bonus_enabled}/>
                    </div>

                    {/* Commission */}
                    <div className="border border-border p-4 space-y-3" data-testid="admin-commission-card">
                        <div className="text-sm">Recurring commission</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldNum label="Base commission %" value={config.pct}
                                onChange={(v) => setConfig({ ...config, pct: v })}
                                testId="admin-commission-pct"/>
                            <FieldNum label="Max billing cycles" hint="Quarterly plan × 4 = 1 year of payouts"
                                value={config.max_cycles}
                                onChange={(v) => setConfig({ ...config, max_cycles: v })}
                                testId="admin-max-cycles"/>
                        </div>
                    </div>

                    {/* Tier */}
                    <div className="border border-border p-4 space-y-3" data-testid="admin-tier-card">
                        <div className="text-sm">Tier 2 bonus</div>
                        <p className="text-[10px] text-muted-foreground">
                            Top referrers earn a bonus percentage on every future commission once they pass the threshold.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldNum label="Tier 2 bonus % (added to base)" value={config.tier_bonus}
                                onChange={(v) => setConfig({ ...config, tier_bonus: v })}
                                testId="admin-tier-bonus"/>
                            <FieldNum label="Conversions needed for Tier 2" value={config.tier_threshold}
                                onChange={(v) => setConfig({ ...config, tier_threshold: v })}
                                testId="admin-tier-threshold"/>
                        </div>
                    </div>

                    {/* Cooling off */}
                    <div className="border border-border p-4 space-y-3" data-testid="admin-cooling-card">
                        <div className="text-sm">Cooling-off window</div>
                        <p className="text-[10px] text-muted-foreground">
                            Commission accrues as <span className="text-primary">pending</span> for this many days before becoming redeemable. Anti-fraud / chargeback protection.
                        </p>
                        <FieldNum label="Cooling-off days" value={config.cooling_days}
                            onChange={(v) => setConfig({ ...config, cooling_days: v })}
                            testId="admin-cooling-days"/>
                    </div>

                    <div className="flex justify-end">
                        <GoldButton onClick={saveConfig} disabled={savingCfg} data-testid="admin-config-save-btn">
                            {savingCfg ? "Saving…" : "Save configuration"}
                        </GoldButton>
                    </div>
                </div>
            )}

            {/* REFERRALS TAB */}
            {tab === "referrals" && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        {[
                            ["all", `All (${allRows.length})`],
                            ["converted", `Converted (${stats.converted})`],
                            ["signed_up", "Signed up"],
                            ["suspicious", `Suspicious (${stats.suspicious})`],
                            ["expired", `Expired (${stats.expired})`],
                        ].map(([k, label]) => (
                            <button key={k} onClick={() => setFilterStatus(k)}
                                data-testid={`admin-filter-${k}`}
                                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border transition-colors ${filterStatus === k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="bg-card border border-border overflow-x-auto" data-testid="admin-referrals-table">
                        <FilterChip ctrl={refCtrl} className="m-3"/>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                                    <SortableTh field="code" ctrl={refCtrl} className="py-3 px-4">Code</SortableTh>
                                    <SortableTh field="new_user_email" ctrl={refCtrl} className="py-3 px-4">Referee email</SortableTh>
                                    <SortableTh field="created_at" ctrl={refCtrl} className="py-3 px-4">Signed up</SortableTh>
                                    <SortableTh field="status" ctrl={refCtrl} className="py-3 px-4">Status</SortableTh>
                                    <SortableTh field="flag" ctrl={refCtrl} className="py-3 px-4">Flag</SortableTh>
                                    <th className="py-3 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {refCtrl.rows.length === 0 ? (
                                    <tr><td colSpan="6" className="py-8 text-center text-xs text-muted-foreground">No referrals match the filter.</td></tr>
                                ) : refCtrl.rows.map(r => (
                                    <tr key={r.id} className="border-b border-border hover:bg-background" data-testid={`admin-referral-row-${r.id}`}>
                                        <td className="py-3 px-4 font-mono text-xs text-primary">{r.code}</td>
                                        <td className="py-3 px-4 text-foreground">{r.new_user_email}</td>
                                        <td className="py-3 px-4 text-muted-foreground text-xs">{fmtDateTime(r.created_at)}</td>
                                        <td className="py-3 px-4">
                                            <span className={r.status === "converted" ? "text-emerald-400" : r.status === "expired" ? "text-muted-foreground" : "text-amber-400"}>
                                                ● {r.status}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            {r.suspicious ? <span className="text-amber-400 text-[10px]">⚠ Suspicious</span> : <span className="text-muted-foreground text-[10px]">—</span>}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            {r.suspicious && (
                                                <button onClick={() => clearReview(r.id)} data-testid={`admin-clear-review-${r.id}`}
                                                    className="text-[10px] text-emerald-400 hover:underline">
                                                    Clear review
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* LEADERBOARD TAB */}
            {tab === "leaderboard" && (
                <div className="bg-card border border-border overflow-x-auto" data-testid="admin-leaderboard-table">
                    <FilterChip ctrl={lbCtrl} className="m-3"/>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                                <SortableTh field="user_name" ctrl={lbCtrl} className="py-3 px-4">Referrer</SortableTh>
                                <SortableTh field="user_email" ctrl={lbCtrl} className="py-3 px-4">Email</SortableTh>
                                <SortableTh field="signups" ctrl={lbCtrl} className="py-3 px-4 text-right" align="right">Signups</SortableTh>
                                <SortableTh field="conversions" ctrl={lbCtrl} className="py-3 px-4 text-right" align="right">Conversions</SortableTh>
                                <SortableTh field="lifetime_earnings" ctrl={lbCtrl} className="py-3 px-4 text-right" align="right">Lifetime earnings</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {lbCtrl.rows.length === 0 ? (
                                <tr><td colSpan="5" className="py-8 text-center text-xs text-muted-foreground">No referrers yet.</td></tr>
                            ) : lbCtrl.rows.map((u, i) => (
                                <tr key={u.user_id} className="border-b border-border" data-testid={`admin-leader-row-${i}`}>
                                    <td className="py-3 px-4 text-foreground">{u.user_name}</td>
                                    <td className="py-3 px-4 text-muted-foreground text-xs">{u.user_email}</td>
                                    <td className="py-3 px-4 text-right">{u.signups}</td>
                                    <td className="py-3 px-4 text-right text-emerald-400">{u.conversions}</td>
                                    <td className="py-3 px-4 text-right text-primary">₹{u.lifetime_earnings.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const Stat = ({ icon: Icon, label, value, hint, tone = "neutral", testId }) => {
    const toneClass = tone === "emerald" ? "text-emerald-400" : tone === "amber" ? "text-amber-400" : "text-foreground";
    return (
        <div className="bg-card border border-border p-5" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <div className="overline">{label}</div>
                <Icon className={`w-4 h-4 ${toneClass}`} />
            </div>
            <div className={`text-2xl font-serif ${toneClass}`}>{value}</div>
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
};

const FieldNum = ({ label, value, onChange, hint, testId, disabled }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">{label}</label>
        <input
            type="number"
            step="0.01"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        />
        {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
);
